import React, { useMemo } from 'react';
import { Scatter } from 'react-chartjs-2';
import { Card } from 'react-bootstrap';
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
    TooltipItem,
    ScriptableContext,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

interface SurveyData {
    start_date?: string;
    eq_1?: number;
    eq_2?: number;
    eq_3?: number;
    eq_4?: number;
    eq_5?: number;
    eq_6?: number;
    se_c?: number;
    visit_2?: number | string;
    visit_4?: number | string | null;
}

interface ScanData {
    survey_daily?: SurveyData[];
}

interface DailyAssessment {
    day: number;
    date: string;
    assessment_data?: {
        trajectory_classification?: string;
    };
    trajectory_classification?: string;
}

interface HeatmapDataPoint {
    x: number;
    y: number;
    v: number;
    metricKey: string;
    metric: string;
    eventData?: {
        encounter: boolean;
        medChange: boolean;
    };
}

interface PatientReportedHeatmapProps {
    scanData?: ScanData;
    selectedDay?: number;
    dailyAssessments?: DailyAssessment[];
}

const PatientReportedHeatmap: React.FC<PatientReportedHeatmapProps> = ({
    scanData,
    selectedDay = -1,
    dailyAssessments = []
}) => {
    // Parse date helper function
    const parsePtDate = (dateStr: string): Date | null => {
        if (!dateStr) return null;
        try {
            // Handle date-only format (YYYY-MM-DD)
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                const [year, month, day] = dateStr.split('-').map(Number);
                const date = new Date(year, month - 1, day, 0, 0, 0, 0);
                if (isNaN(date.getTime())) return null;
                return date;
            }

            // Handle YY-MM-DD format (two-digit year) 
            if (/^\d{2}-\d{2}-\d{2}$/.test(dateStr)) {
                const [yearShort, month, day] = dateStr.split('-').map(Number);
                const year = yearShort <= 50 ? 2000 + yearShort : 1900 + yearShort;
                const date = new Date(year, month - 1, day, 0, 0, 0, 0);
                if (isNaN(date.getTime())) return null;
                return date;
            }

            // Extract datetime portion with 4-digit year
            const match4 = dateStr.match(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/);
            if (match4 && match4[1]) {
                const [datePart, timePart] = match4[1].split(' ');
                const [year, month, day] = datePart.split('-').map(Number);
                const [hour, minute, second] = timePart.split(':').map(Number);
                const date = new Date(year, month - 1, day, hour, minute, second);
                if (isNaN(date.getTime())) return null;
                return date;
            }

            // Extract datetime with 2-digit year (YY-MM-DD HH:MM:SS)
            const match2 = dateStr.match(/(\d{2}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/);
            if (match2 && match2[1]) {
                const [datePart, timePart] = match2[1].split(' ');
                const [yearShort, month, day] = datePart.split('-').map(Number);
                const year = yearShort <= 50 ? 2000 + yearShort : 1900 + yearShort;
                const [hour, minute, second] = timePart.split(':').map(Number);
                const date = new Date(year, month - 1, day, hour, minute, second);
                if (isNaN(date.getTime())) return null;
                return date;
            }

            // Fallback - try direct parsing after cleaning
            const cleanedDate = dateStr.replace(' PT', '').replace(' PST', '').replace(' PDT', '');
            let date = new Date(cleanedDate);

            if (isNaN(date.getTime()) || date.getFullYear() < 1900 || date.getFullYear() > 2100) {
                const altMatch = cleanedDate.match(/(\d{1,4})[-\/](\d{1,2})[-\/](\d{1,4})/);
                if (altMatch) {
                    let [_, part1, part2, part3] = altMatch;
                    let year, month, day;

                    if (part1.length === 4) {
                        year = parseInt(part1);
                        month = parseInt(part2);
                        day = parseInt(part3);
                    } else if (part3.length === 4) {
                        year = parseInt(part3);
                        month = parseInt(part1);
                        day = parseInt(part2);
                    } else {
                        year = parseInt(part1);
                        if (year < 100) {
                            year = year <= 50 ? 2000 + year : 1900 + year;
                        }
                        month = parseInt(part2);
                        day = parseInt(part3);
                    }

                    date = new Date(year, month - 1, day, 0, 0, 0, 0);
                }
            }

            if (isNaN(date.getTime())) return null;
            return date;
        } catch (e) {
            console.error('Error parsing date:', dateStr, e);
            return null;
        }
    };

    // Floor date to midnight
    const floorDate = (date: Date): Date | null => {
        if (!date) return null;
        const floored = new Date(date);
        floored.setHours(0, 0, 0, 0);
        return floored;
    };

    // Get enrollment date from daily assessments
    const getEnrollmentDate = (): Date | null => {
        if (dailyAssessments && dailyAssessments.length > 0) {
            const day0Assessment = dailyAssessments.find(a => a.day === 0);
            if (day0Assessment) {
                return parsePtDate(day0Assessment.date);
            }
        }
        return null;
    };

    // Prepare chart data and options
    const { chartData, chartOptions } = useMemo(() => {
        const surveyDaily = scanData?.survey_daily || [];

        if (surveyDaily.length === 0) {
            console.log("Survey Data is empty");
            return { chartData: null, chartOptions: null };
        }

        // Prepare heatmap data - order matters for display (from top to bottom)
        const metrics = ['eq_6', 'se_c', 'eq_1', 'eq_2', 'eq_3', 'eq_4', 'eq_5', 'events'];
        const metricLabels = ['Overall health', 'Self-efficacy', 'Mobility', 'Self-care', 'Usual activities', 'Pain/Discomfort', 'Anxiety/Depression', 'Events'];

        const heatmapData: any = [];
        const dateSet = new Set<number>();

        // Get enrollment date and calculate date range
        const enrollmentDate = getEnrollmentDate();

        // Process survey data
        surveyDaily.forEach(survey => {
            const dateStr = survey.start_date;
            if (!dateStr) return;

            const date = parsePtDate(dateStr);
            if (!date) return;

            const flooredDate = floorDate(date)?.valueOf();
            if (!flooredDate) return;
            dateSet.add(flooredDate);

            // Add metric values
            metrics.forEach((key, metricIndex) => {
                if (key === 'events') {
                    // Add event markers for healthcare encounters or medication changes
                    // console.log("HEATMAP MEDICATION CHANGES");
                    if (survey.visit_2 || survey.visit_4) {
                        heatmapData.push({
                            x: flooredDate,
                            y: metricIndex,
                            v: 1,
                            metricKey: key,
                            metric: metricLabels[metricIndex],
                            eventData: {
                                encounter: survey.visit_2 === 1 || survey.visit_2 === '1',
                                medChange: survey.visit_4 !== null && survey.visit_4 !== undefined && survey.visit_4 !== 0
                            }
                        });
                    }
                } else {
                    // @ts-ignore
                    const value = survey[key];
                    if (value !== null && value !== undefined) {
                        heatmapData.push({
                            x: flooredDate,
                            y: metricIndex,
                            v: parseFloat(value),
                            metricKey: key,
                            metric: metricLabels[metricIndex]
                        });
                    }
                }
            });
        });

        if (heatmapData.length === 0) {
            console.log("Heatmap is empty");
            return { chartData: null, chartOptions: null };
        }

        
        if (!enrollmentDate) {
            console.log("Enrollment date is empty");
            return { chartData: null, chartOptions: null };
        }
        let fd = floorDate(enrollmentDate);
        if (!fd) {
            console.log("FD is empty");
            return { chartData: null, chartOptions: null };
        }

        // Calculate shared date range
        const baselineDate = enrollmentDate ? fd.valueOf() : (dateSet.size > 0 ? Math.min(...dateSet) : Date.now());
        const sharedMinDate = baselineDate - 86400000; // Add one day padding before
        const sharedMaxDate = dateSet.size > 0 ? Math.max(...dateSet) + 86400000 : Date.now() + 86400000;

        // Transform data for Chart.js scatter plot with custom rendering
        const datasets = [{
            label: 'Patient-reported metrics',
            data: heatmapData.map((point: any) => ({
                x: point.x,
                y: point.y,
                ...point // Include all original data for tooltips and custom rendering
            })),
            backgroundColor: (context: ScriptableContext<'scatter'>) => {
                const point = context.raw as HeatmapDataPoint;
                if (!point) return 'rgba(0,0,0,0)';

                if (point.metricKey === 'events') {
                    return 'rgba(147, 51, 234, 0.8)'; // Purple for events
                }

                // Calculate color based on normalized value
                let normalizedValue: number;
                if (point.metricKey && point.metricKey.startsWith('eq_') && point.metricKey !== 'eq_6') {
                    normalizedValue = (point.v - 1) / 4; // EQ5D components: 1=good, 5=poor
                } else if (point.metricKey === 'eq_6') {
                    normalizedValue = 1 - (point.v / 100); // Overall health: 0=poor, 100=good (reverse scale)
                } else if (point.metricKey === 'se_c') {
                    normalizedValue = 1 - (point.v / 10); // Self-efficacy: 0=poor, 10=good (reverse scale)
                } else {
                    normalizedValue = 0.5; // Default for unknown metrics
                }

                // Color gradient from green (good) to yellow to red (poor)
                let r: number, g: number, b: number;
                if (normalizedValue <= 0.5) {
                    const ratio = normalizedValue * 2;
                    r = Math.round(34 + (250 - 34) * ratio);
                    g = Math.round(197 + (204 - 197) * ratio);
                    b = Math.round(94 + (21 - 94) * ratio);
                } else {
                    const ratio = (normalizedValue - 0.5) * 2;
                    r = Math.round(250 + (239 - 250) * ratio);
                    g = Math.round(204 + (68 - 204) * ratio);
                    b = Math.round(21 + (68 - 21) * ratio);
                }

                return `rgba(${r}, ${g}, ${b}, 0.8)`;
            },
            borderColor: 'rgba(255, 255, 255, 0.8)',
            borderWidth: 1,
            pointRadius: 9,
            pointHoverRadius: 10
        }];

        const options = {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    top: 40,
                    bottom: 10,
                    left: 10,
                    right: 10
                }
            },
            interaction: {
                mode: 'point' as const,
                intersect: false
            },
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    enabled: true,
                    displayColors: false,
                    mode: 'point' as const,
                    intersect: false,
                    callbacks: {
                        title: function (tooltipItems: TooltipItem<'scatter'>[]) {
                            if (!tooltipItems || tooltipItems.length === 0) return '';
                            const item = tooltipItems[0];
                            const point = item.raw as HeatmapDataPoint;
                            const date = new Date(point.x);
                            const daysSinceBaseline = Math.round((date.valueOf() - baselineDate) / 86400000);
                            return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (Day ${daysSinceBaseline})`;
                        },
                        label: function (context: TooltipItem<'scatter'>) {
                            const point = context.raw as HeatmapDataPoint;
                            if (!point) return '';

                            if (point.metricKey === 'events') {
                                const labels: string[] = [];
                                if (point.eventData && point.eventData.encounter) labels.push('Healthcare encounter');
                                if (point.eventData && point.eventData.medChange) labels.push('Medication change');
                                return labels.length > 0 ? labels : ['Event'];
                            }

                            let valueText = point.v.toString();
                            if (point.metricKey === 'eq_6') {
                                valueText = `${Math.round(point.v)}/100`;
                            } else if (point.metricKey === 'se_c') {
                                valueText = `${Math.round(point.v)}/10`;
                            } else if (point.metricKey && point.metricKey.startsWith('eq_')) {
                                valueText = `${point.v}/5`;
                            }
                            return `${point.metric}: ${valueText}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    type: 'linear' as const,
                    min: sharedMinDate,
                    max: sharedMaxDate,
                    grid: {
                        display: true,
                        color: 'rgba(0, 0, 0, 0.1)'
                    },
                    ticks: {
                        stepSize: 86400000,
                        autoSkip: false,
                        maxRotation: 0,
                        callback: function (value: number | string) {
                            const numValue = typeof value === 'string' ? parseFloat(value) : value;
                            const date = new Date(numValue);
                            const day = date.getDate();
                            const month = date.toLocaleDateString('en-US', { month: 'short' });
                            return [month, day.toString()]; // 👈 month on top, day below
                        }
                    }
                },
                x2: {
                    type: 'linear' as const,
                    position: 'bottom' as const,
                    min: sharedMinDate,
                    max: sharedMaxDate,
                    grid: { display: false },
                    ticks: {
                        stepSize: 86400000,
                        autoSkip: false,
                        callback: function (value: number | string) {
                            const numValue = typeof value === 'string' ? parseFloat(value) : value;
                            const daysSinceBaseline = Math.round((numValue - baselineDate) / 86400000);
                            return daysSinceBaseline >= 0 ? `(Day ${daysSinceBaseline})` : '';
                        }
                    }
                },
                y: {
                    type: 'linear' as const,
                    min: -1,
                    max: metrics.length,
                    grid: { display: false },
                    ticks: {
                        stepSize: 1,
                        callback: function (value: number | string) {
                            const numValue = typeof value === 'string' ? parseFloat(value) : value;
                            return metricLabels[Math.floor(numValue)] || '';
                        }
                    }
                }
            }
        };

        return { chartData: { datasets }, chartOptions: options };
    }, [scanData, selectedDay, dailyAssessments]);

    const customValueLabels = {
        id: 'customValueLabels',
        afterDatasetsDraw(chart: any) {
            const { ctx } = chart;
            chart.data.datasets.forEach((dataset: any, datasetIndex: any) => {
                const meta = chart.getDatasetMeta(datasetIndex);
                meta.data.forEach((point: any, index: any) => {
                    const raw = dataset.data[index] as any;
                    let valueText = raw.v.toString();

                    if (raw.metricKey === 'events') {
                        // console.log("raw.eventData", raw.eventData);
                        if (raw.eventData && raw.eventData.encounter) valueText = "E";
                        if (raw.eventData && raw.eventData.medChange) valueText = "M";
                    } else {
                        if (raw.metricKey === 'eq_6') valueText = `${Math.round(raw.v)}`;
                        else if (raw.metricKey === 'se_c') valueText = `${Math.round(raw.v)}`;
                        else if (raw.metricKey && raw.metricKey.startsWith('eq_')) valueText = `${raw.v}`;
                    }

                    ctx.save();
                    ctx.fillStyle = 'white';
                    ctx.font = '12px sans-serif';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(valueText, point.x, point.y);
                    ctx.restore();
                });
            });
        }
    };

    // Render gradient legend
    const renderGradientLegend = () => (
        <div className="d-flex justify-content-center align-items-center mb-3">
            <span className="me-2 small">Good</span>
            <div
                style={{
                    width: '200px',
                    height: '15px',
                    background: 'linear-gradient(to right, rgba(34, 197, 94, 0.8), rgba(250, 204, 21, 0.8), rgba(239, 68, 68, 0.8))',
                    border: '1px solid #ccc',
                    borderRadius: '3px'
                }}
            ></div>
            <span className="ms-2 small">Poor</span>
        </div>
    );

    if (!chartData) {
        return (
            <Card>
                <Card.Body>
                    <Card.Title>Patient-reported Metrics</Card.Title>
                    <div className="text-center text-muted py-5">
                        No patient-reported data available
                    </div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card>
            <Card.Body>
                <Card.Title>Patient-reported Metrics</Card.Title>
                {renderGradientLegend()}
                <div style={{ height: '400px', position: 'relative' }}>
                    {/* @ts-ignore */}
                    <Scatter data={chartData} options={chartOptions} plugins={[customValueLabels]} />
                </div>
                <div className="mt-2 small text-muted">
                    <div className="d-flex justify-content-between">
                        <span>E = Healthcare encounter</span>
                        <span>M = Medication change</span>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

export default PatientReportedHeatmap;
